import copy from "copy-to-clipboard";
import dayjs from "dayjs";
import { formatDateRome } from "../../UI/Utils/formatDateRome.ts";
import { X } from "lucide-react";
import { Dialog } from "@headlessui/react";
import { OutlinedButton } from "@normasteaching/norma-ui";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { ExpandableCard } from "../../UI/ExpandableCard.tsx";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import { useMessageController } from "../../UI/useMessageController.ts";
import {
  BonificoOrderDTO,
  BonificoOrderStatus,
  externalSettlementGatewayLabel,
  externalSettlementKindLabel,
  purchasedItemSnapshotOf,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import {
  formatBusinessAddress,
  hasBonificoInvoiceBeenIssued,
  issuesInvoiceOnPayment,
} from "../../ProductService/BonificoOrder/bonificoBilling.ts";
import { usePurchasedItemNames } from "../../ProductService/PurchasedItem/usePurchasedItemNames.ts";
import { CopyableText } from "../../UI/CopyableText.tsx";
import { classNames } from "../../UI/Utils/classNames.ts";
import { businessTypeLabels } from "../../MoneyTransfer/BusinessType.tsx";
import {
  billingCountryNameOf,
  isForeignBillingCountry,
} from "../../MoneyTransfer/billingCountries.ts";
import { BonificoOrderStatusBadge } from "./BonificoOrderStatusBadge.tsx";
import {
  ExternalSettlementDetails,
  InstallmentsSettlementDetails,
} from "./InstallmentsSettlementDetails.tsx";
import { B2BContractDetails } from "./B2BContractDetails.tsx";
import { BonificoOrderNotes } from "./BonificoOrderNotes.tsx";
import { InvoiceItemDescriptionEditor } from "./InvoiceItemDescriptionEditor.tsx";
import { Article7TerBadge } from "./Article7TerBadge.tsx";
import {
  describeIssuedInvoiceVat,
  isArticle7Ter,
} from "../../ProductService/BonificoOrder/article7Ter.ts";
import {
  BonificoOrderActionHandlers,
  BonificoOrderActions,
} from "./BonificoOrderActions.tsx";

type Props = {
  order: BonificoOrderDTO;
  onClose: () => void;
  actionHandlers: BonificoOrderActionHandlers;
  onEditBilling?: (order: BonificoOrderDTO) => void;
  onOrderUpdated?: (order: BonificoOrderDTO) => void;
};

function BillingDetailRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div>
      <span className="font-medium text-gray-700">{label}:</span>
      <p className="text-gray-600">{value}</p>
    </div>
  );
}

function formatEuros(amountInCents: number): string {
  return `€${(amountInCents / 100).toFixed(2)}`;
}

export function BonificoOrderDetailsModal({
  order,
  onClose,
  actionHandlers,
  onEditBilling,
  onOrderUpdated,
}: Props) {
  const { showMessage, message, open } = useMessageController();
  const { resolveName } = usePurchasedItemNames();
  const amountPaid = order.amountPaidInCents;
  const listPriceInCents =
    order.pricing?.listPriceInCents.amount ??
    (amountPaid && order.coupon
      ? amountPaid.amount + order.coupon.value
      : undefined);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Dettagli Ordine Bonifico"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-4xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      <SuccessMessage open={open} message={message} />
      <div className="space-y-6 max-h-[70vh] overflow-y-auto max-w-4xl">
        {/* Informazioni Generali */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Informazioni Generali
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium text-gray-700">Order ID:</span>
              <p
                className="text-gray-600 font-mono text-xs cursor-pointer hover:text-blue-600 truncate"
                title={order.orderId}
                onClick={() => {
                  copy(order.orderId);
                  showMessage({
                    message: "ID dell'ordine copiato negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {order.orderId}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Data creazione:</span>
              <p className="text-gray-600">
                {dayjs(order.createdAt).format("DD/MM/YYYY HH:mm")}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Stato:</span>
              <div className="mt-1">
                <BonificoOrderStatusBadge status={order.status} />
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700">
                {order.b2bContract
                  ? "Importo del contratto:"
                  : "Importo pagato:"}
              </span>
              {amountPaid ? (
                <div className="flex flex-col">
                  {listPriceInCents !== undefined &&
                    listPriceInCents !== amountPaid.amount && (
                      <p className="text-gray-400 line-through text-sm">
                        {formatEuros(listPriceInCents)}{" "}
                        {amountPaid.currency.toUpperCase()}
                      </p>
                    )}
                  <p className="text-gray-600 font-medium">
                    {formatEuros(amountPaid.amount)}{" "}
                    {amountPaid.currency.toUpperCase()}
                  </p>
                  {order.pricing?.isAmountManuallySet && (
                    <p className="text-xs text-orange-600">
                      Importo modificato a mano
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-600">Da definire alla conferma</p>
              )}
            </div>
            {order.normasteachingProductId && order.productType && (
              <>
                <div>
                  <span className="font-medium text-gray-700">Tipo:</span>
                  <p className="text-gray-600 capitalize">
                    {order.productType === "product" ? "Prodotto" : "Bundle"}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    {order.productType === "bundle" ? "Bundle:" : "Prodotto:"}
                  </span>
                  {(() => {
                    const purchasedItem = resolveName({
                      id: order.normasteachingProductId!,
                      type: order.productType!,
                      snapshot: purchasedItemSnapshotOf(order),
                    });
                    return (
                      <p className="text-gray-600 truncate">
                        <CopyableText
                          className={classNames(
                            "cursor-pointer hover:text-blue-600",
                            // only a raw id deserves monospace
                            purchasedItem.source === "id"
                              ? "font-mono text-xs"
                              : undefined,
                          )}
                          value={order.normasteachingProductId!}
                          title={order.normasteachingProductId!}
                          copyMessage={`ID del ${order.productType === "bundle" ? "bundle" : "prodotto"} copiato negli appunti!`}
                          showMessage={showMessage}
                        >
                          {purchasedItem.name}
                        </CopyableText>
                      </p>
                    );
                  })()}
                </div>
              </>
            )}
            {order.receiptDateUtc && (
              <div>
                <span className="font-medium text-gray-700">
                  Data contabile:
                </span>
                <p className="text-gray-600">
                  {dayjs(order.receiptDateUtc).format("DD/MM/YYYY")}
                </p>
              </div>
            )}
            {order.confirmedAtUtc && (
              <div>
                <span className="font-medium text-gray-700">
                  Confermato il:
                </span>
                <p className="text-gray-600">
                  {dayjs(order.confirmedAtUtc).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>
            )}
            {order.valueDateUtc && (
              <div>
                <span className="font-medium text-gray-700">Data valuta:</span>
                <p className="text-gray-600">
                  {dayjs(order.valueDateUtc).format("DD/MM/YYYY")}
                </p>
              </div>
            )}
            {order.paidAtUtc && (
              <div>
                <span className="font-medium text-gray-700">
                  Segnato come pagato il:
                </span>
                <p className="text-gray-600">
                  {dayjs(order.paidAtUtc).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>
            )}
            {order.invoiceIssuedAtUtc && (
              <div>
                <span className="font-medium text-gray-700">
                  Fattura emessa il:
                </span>
                <p className="text-gray-600">
                  {dayjs(order.invoiceIssuedAtUtc).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>
            )}
            {order.oneTimeNumberOfInstallments && (
              <div>
                <span className="font-medium text-gray-700">Rate:</span>
                <p className="text-gray-600">
                  {order.oneTimeNumberOfInstallments}
                </p>
              </div>
            )}
          </div>
        </div>

        <ExpandableCard title="Note" defaultExpanded>
          <BonificoOrderNotes
            key={order.orderId}
            order={order}
            onSaved={onOrderUpdated}
          />
        </ExpandableCard>

        {issuesInvoiceOnPayment(order) && (
          <ExpandableCard title="Descrizione in fattura" defaultExpanded>
            <InvoiceItemDescriptionEditor
              key={order.orderId}
              order={order}
              onSaved={onOrderUpdated}
            />
          </ExpandableCard>
        )}

        {order.b2bContract && (
          <ExpandableCard title="Contratto B2B" defaultExpanded>
            <B2BContractDetails contract={order.b2bContract} />
          </ExpandableCard>
        )}

        {order.installmentsSettlement && (
          <ExpandableCard title="Saldo rate ordine Stripe" defaultExpanded>
            <InstallmentsSettlementDetails
              settlement={order.installmentsSettlement}
              settlementAmount={order.amountPaidInCents ?? undefined}
            />
          </ExpandableCard>
        )}

        {order.externalSettlement && (
          <ExpandableCard
            title={`${externalSettlementKindLabel(order.externalSettlement)} ordine ${externalSettlementGatewayLabel(order.externalSettlement)}`}
            defaultExpanded
          >
            <ExternalSettlementDetails
              settlement={order.externalSettlement}
              settlementAmount={order.amountPaidInCents ?? undefined}
            />
          </ExpandableCard>
        )}

        <ExpandableCard title="Informazioni Cliente">
          <div>
            <span className="font-medium text-gray-700">Nome:</span>
            <p className="text-gray-600">
              {order.customer.firstName} {order.customer.lastName}
            </p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Email:</span>
            <p
              className="text-gray-600 cursor-pointer hover:text-blue-600"
              onClick={() => {
                copy(order.customer.email);
                showMessage({
                  message: "Email copiata negli appunti!",
                  timeoutMs: 4000,
                });
              }}
            >
              {order.customer.email}
            </p>
          </div>
          {order.customer.taxCode && (
            <div>
              <span className="font-medium text-gray-700">Codice Fiscale:</span>
              <p className="text-gray-600">{order.customer.taxCode}</p>
            </div>
          )}
        </ExpandableCard>

        {/* Informazioni Fatturazione */}
        {order.businessInformation && (
          <ExpandableCard title="Informazioni Fatturazione">
            <BillingDetailRow
              label="Tipo entità"
              value={
                businessTypeLabels[order.businessInformation.businessType] ??
                order.businessInformation.businessType
              }
            />
            <BillingDetailRow
              label="Paese"
              value={billingCountryNameOf(
                order.businessInformation.businessAddress.country,
              )}
            />
            <BillingDetailRow
              label="Ragione sociale"
              value={order.businessInformation.businessName}
            />
            <BillingDetailRow
              label={
                isForeignBillingCountry(
                  order.businessInformation.businessAddress.country,
                )
                  ? "Tax ID"
                  : "Partita IVA"
              }
              value={order.businessInformation.vatCode}
            />
            <BillingDetailRow
              label="Codice fiscale"
              value={order.customer.taxCode}
            />
            <BillingDetailRow
              label="Indirizzo"
              value={formatBusinessAddress(
                order.businessInformation.businessAddress,
              )}
            />
            <BillingDetailRow
              label="Codice SDI"
              value={order.businessInformation.sdiCode}
            />
            <BillingDetailRow
              label="PEC"
              value={order.businessInformation.pec}
            />
            {isArticle7Ter(order.vatExemption) && (
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">IVA:</span>
                <Article7TerBadge />
              </div>
            )}
            {describeIssuedInvoiceVat(order) && (
              <p className="text-xs text-gray-500">
                {describeIssuedInvoiceVat(order)}
              </p>
            )}
            {hasBonificoInvoiceBeenIssued(order) && (
              <p className="text-xs text-gray-500">
                La fattura è già stata emessa: le correzioni valgono per le note
                di credito dei rimborsi successivi.
              </p>
            )}
            {onEditBilling && (
              <button
                type="button"
                onClick={() => onEditBilling(order)}
                className="self-start text-sm text-blue-600 hover:underline"
              >
                Modifica dati di fatturazione
              </button>
            )}
          </ExpandableCard>
        )}

        {/* Coupon */}
        {order.coupon && (
          <ExpandableCard title="Coupon Applicato">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Codice:</span>
              <code className="bg-white px-2 py-1 rounded border border-gray-300 text-gray-700 font-mono">
                {order.coupon.couponCode}
              </code>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Sconto:</span>
              <span className="text-green-700 font-medium">
                -€{(order.coupon.value / 100).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                Prezzo originale:
              </span>
              <span className="text-gray-400 line-through text-sm">
                {listPriceInCents !== undefined
                  ? formatEuros(listPriceInCents)
                  : "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Prezzo finale:</span>
              <span className="text-gray-700 font-medium">
                {amountPaid ? formatEuros(amountPaid.amount) : "—"}
              </span>
            </div>
          </ExpandableCard>
        )}

        {/* Partecipanti Bundle */}
        {order.bundleParticipants && order.bundleParticipants.length > 0 && (
          <ExpandableCard title="Partecipanti Bundle">
            {order.bundleParticipants.map((participant, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-md space-y-2">
                <div className="font-medium text-gray-900">
                  Partecipante {index + 1}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Nome:</span>{" "}
                  {participant.customer.firstName}{" "}
                  {participant.customer.lastName}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Email:</span>{" "}
                  <span
                    className="text-gray-600 cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      copy(participant.customer.email);
                      showMessage({
                        message: "Email copiata negli appunti!",
                        timeoutMs: 4000,
                      });
                    }}
                  >
                    {participant.customer.email}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-sm text-gray-700">Prodotti:</span>
                  {participant.products.map((product, pIndex) => {
                    const componentProduct = resolveName({
                      id: product.id,
                      type: "product",
                    });
                    return (
                      <div key={pIndex} className="text-sm text-gray-600 ml-3">
                        •{" "}
                        <CopyableText
                          className={classNames(
                            "cursor-pointer hover:text-blue-600",
                            componentProduct.source === "id"
                              ? "font-mono text-xs"
                              : undefined,
                          )}
                          value={product.id}
                          title={product.id}
                          copyMessage="ID del prodotto copiato negli appunti!"
                          showMessage={showMessage}
                        >
                          {componentProduct.name}
                        </CopyableText>{" "}
                        — €{(product.priceInCents.amount / 100).toFixed(2)}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </ExpandableCard>
        )}

        {/* Altri Partecipanti */}
        {order.participants && order.participants.length > 0 && (
          <ExpandableCard title="Altri Partecipanti">
            {order.participants.map((participant, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-md space-y-1">
                <div>
                  <span className="text-sm text-gray-700">Nome:</span>{" "}
                  {participant.firstName} {participant.lastName}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Email:</span>{" "}
                  <span
                    className="text-gray-600 cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      copy(participant.email);
                      showMessage({
                        message: "Email copiata negli appunti!",
                        timeoutMs: 4000,
                      });
                    }}
                  >
                    {participant.email}
                  </span>
                </div>
              </div>
            ))}
          </ExpandableCard>
        )}

        {/* Rimborso */}
        {(order.status === BonificoOrderStatus.Refunded ||
          order.status === BonificoOrderStatus.PartiallyRefunded) &&
          order.refundedAmountInCents && (
            <ExpandableCard title="Rimborso">
              <div>
                <span className="font-medium text-gray-700">
                  Importo rimborsato:
                </span>
                <p className="text-gray-600 font-medium">
                  €{(order.refundedAmountInCents.amount / 100).toFixed(2)}{" "}
                  {order.refundedAmountInCents.currency.toUpperCase()}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Tipo:</span>
                <p className="text-gray-600">
                  {order.status === BonificoOrderStatus.Refunded
                    ? "Rimborso totale"
                    : "Rimborso parziale"}
                </p>
              </div>
              {order.refundedAtUtc && (
                <div>
                  <span className="font-medium text-gray-700">Data:</span>
                  <p className="text-gray-600">
                    {formatDateRome(order.refundedAtUtc)}
                  </p>
                </div>
              )}
            </ExpandableCard>
          )}

        {/* Pulsanti */}
        <div className="flex items-center justify-between gap-6 pt-2">
          <OutlinedButton
            iconLeft={<X className="w-5" />}
            onClick={onClose}
            label="Chiudi"
          />
          <BonificoOrderActions
            order={order}
            handlers={actionHandlers}
            size="md"
          />
        </div>
      </div>
    </Modal>
  );
}
